import * as React from 'react';
import useProductName from 'brand-customization/useProductName';

import Modal from 'components/bootstrap/Modal';
import type { InvestigationAPIType } from 'security-app/hooks/api/investigationsAPI.types';
import DisclaimerModalContent from 'security-app/components/Investigations/AIReport/DisclaimerModalContent';
import AIReportModalContent from 'security-app/components/Investigations/AIReport/AIReportModalContent';
import useIsInvestigationAIReportEnabled from 'security-app/components/Investigations/AIReport/hooks/useIsInvestigationAIReportEnabled';
import useAITermsAcceptance from 'ai/hooks/useAITermsAcceptance';
import Spinner from 'components/common/Spinner';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import Alert from 'components/bootstrap/Alert';
import Button from 'components/bootstrap/Button';
import ModalButtonToolbar from 'components/common/ModalButtonToolbar';

type Props = {
  onCancel: () => void;
  investigation: InvestigationAPIType;
};

const telemetryEvents = {
  cancel: TELEMETRY_EVENT_TYPE.SECURITY_APP.INVESTIGATION_REPORT_BY_AI.TERMS_AND_CONDITIONS_CANCELED,
  consent: TELEMETRY_EVENT_TYPE.SECURITY_APP.INVESTIGATION_REPORT_BY_AI.TERMS_AND_CONDITIONS_CONSENT,
};

type TermsAcceptanceErrorProps = { error: string; onCancel: () => void };

const TermsAcceptanceError = ({ error, onCancel }: TermsAcceptanceErrorProps) => {
  const productName = useProductName();

  return (
    <>
      <Modal.Header>
        <h3>Error</h3>
      </Modal.Header>
      <Modal.Body>
        The following error occurred while fetching the Terms for AI functionality. Please check general connectivity
        between your {productName} server and the AI service and/or your {productName} server logs;
        <Alert bsStyle="danger">{error}</Alert>
      </Modal.Body>
      <Modal.Footer>
        <ModalButtonToolbar>
          <Button onClick={onCancel}>Close</Button>
        </ModalButtonToolbar>
      </Modal.Footer>
    </>
  );
};

const AIReportModal = ({ onCancel, investigation }: Props) => {
  const isAIReportFeatureEnabled = useIsInvestigationAIReportEnabled();
  const {
    data: { accepted, error },
    isLoading: loadingAcceptance,
    refetch,
  } = useAITermsAcceptance();

  const onConsent = () => {
    refetch();
  };

  return loadingAcceptance ? (
    <Spinner />
  ) : (
    <Modal show={isAIReportFeatureEnabled} onHide={onCancel} bsSize="large">
      {/* eslint-disable-next-line no-nested-ternary */}
      {error ? (
        <TermsAcceptanceError error={error} onCancel={onCancel} />
      ) : accepted ? (
        <AIReportModalContent onClose={onCancel} investigation={investigation} />
      ) : (
        <DisclaimerModalContent onClose={onCancel} onConsent={onConsent} telemetryEvents={telemetryEvents} />
      )}
    </Modal>
  );
};

export default AIReportModal;
